import { headingIdFromText } from './content-sidebar.js'

/** Minimum h2 count required before the table of contents is shown. */
export const POLICY_TOC_MIN_HEADINGS = 2

/** Sticky header offset for in-page anchor scrolling (matches .page-content h2 scroll-margin). */
export const POLICY_TOC_SCROLL_OFFSET_PX = 80

/**
 * @typedef {{ id: string, label: string }} PolicyTocLink
 */

/**
 * @param {Array<{ id?: string, textContent?: string | null }>} headings
 * @returns {PolicyTocLink[]}
 */
export function buildPolicyTocLinks(headings) {
  return headings
    .map((heading, index) => {
      const label = (heading.textContent || '').trim()
      if (!label) return null

      const id =
        heading.id && heading.id.trim()
          ? heading.id.trim()
          : headingIdFromText(label, index)

      return { id, label }
    })
    .filter(Boolean)
}

/**
 * @param {number} count
 * @returns {boolean}
 */
export function shouldShowPolicyToc(count) {
  return count >= POLICY_TOC_MIN_HEADINGS
}

/**
 * @param {HTMLElement} nav
 * @param {PolicyTocLink[]} links
 */
export function renderPolicyTocList(nav, links) {
  const list = nav.querySelector('.policy-toc__list')
  if (!list) return

  list.replaceChildren()

  for (const { id, label } of links) {
    const li = document.createElement('li')
    const a = document.createElement('a')
    a.href = `#${id}`
    a.textContent = label
    li.appendChild(a)
    list.appendChild(li)
  }
}

/**
 * @param {HTMLElement} heading
 * @param {string} id
 */
export function ensureHeadingId(heading, id) {
  if (!heading.id) {
    heading.id = id
  }
}

/**
 * @param {string} targetId
 * @param {number} [offsetPx]
 */
export function scrollToPolicySection(
  targetId,
  offsetPx = POLICY_TOC_SCROLL_OFFSET_PX
) {
  const target = document.getElementById(targetId)
  if (!target) return

  const top = target.getBoundingClientRect().top + window.pageYOffset - offsetPx

  window.scrollTo({ top, behavior: 'smooth' })
}

/** Viewport width (px) below which the table of contents collapses into a panel. */
export const POLICY_TOC_MOBILE_MAX_WIDTH_PX = 989

/**
 * Mark the table-of-contents link for `id` as active and clear the others.
 *
 * @param {HTMLElement} nav
 * @param {string} id
 */
export function setActiveTocLink(nav, id) {
  if (!nav) return

  nav.querySelectorAll('.policy-toc__list a').forEach((anchor) => {
    const isActive = anchor.getAttribute('href') === `#${id}`
    anchor.classList.toggle('is-active', isActive)
    if (isActive) {
      anchor.setAttribute('aria-current', 'true')
    } else {
      anchor.removeAttribute('aria-current')
    }
  })
}

/**
 * Open the FAQ `<details>` that contains (or is) the element targeted by a hash.
 *
 * @param {ParentNode} root
 * @param {string} [hash]
 */
export function openFaqForHash(root, hash) {
  if (!root || !hash || !hash.startsWith('#')) return

  const id = hash.slice(1)
  if (!id) return

  const target =
    typeof root.getElementById === 'function'
      ? root.getElementById(id)
      : root.querySelector(`#${CSS.escape(id)}`)

  if (!target || typeof target.closest !== 'function') return

  const details = target.matches('details.policy-faq__item')
    ? target
    : target.closest('details.policy-faq__item')

  if (details) details.open = true
}

/**
 * Configure the collapsible toggle: a disclosure on mobile, always-open on desktop.
 *
 * @param {HTMLElement} nav
 */
function setupTocDisclosure(nav) {
  const toggle = nav.querySelector('.policy-toc__toggle')
  const panel = nav.querySelector('.policy-toc__panel')
  if (!toggle || !panel) return

  const mobileQuery = window.matchMedia(
    `(max-width: ${POLICY_TOC_MOBILE_MAX_WIDTH_PX}px)`
  )

  const applyMode = () => {
    if (mobileQuery.matches) {
      toggle.setAttribute('aria-expanded', 'false')
      panel.hidden = true
    } else {
      toggle.removeAttribute('aria-expanded')
      panel.hidden = false
    }
  }

  toggle.addEventListener('click', () => {
    if (!mobileQuery.matches) return
    const expanded = toggle.getAttribute('aria-expanded') === 'true'
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true')
    panel.hidden = expanded
  })

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', applyMode)
  }

  applyMode()
}

/**
 * @param {HTMLElement} nav
 * @param {HTMLElement[]} headings
 */
function setupScrollSpy(nav, headings) {
  if (typeof IntersectionObserver !== 'function' || headings.length === 0) {
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTocLink(nav, entry.target.id)
        }
      })
    },
    { root: null, rootMargin: '-10% 0px -80% 0px', threshold: 0 }
  )

  headings.forEach((heading) => observer.observe(heading))
}

/**
 * @param {ParentNode} [root]
 */
export function initPolicyToc(root = document) {
  const nav = root.querySelector('.policy-toc:not([data-policy-toc-ready])')
  const pageContent = root.querySelector('.page-content')

  if (!nav || !pageContent) return

  const headings = [...pageContent.querySelectorAll('h2')]
  const links = buildPolicyTocLinks(headings)

  if (!shouldShowPolicyToc(links.length)) {
    nav.hidden = true
    return
  }

  links.forEach((link, index) => {
    const heading = headings[index]
    if (heading) {
      ensureHeadingId(heading, link.id)
    }
  })

  renderPolicyTocList(nav, links)
  nav.hidden = false

  nav.querySelectorAll('.policy-toc__list a').forEach((anchor) => {
    anchor.addEventListener('click', (event) => {
      event.preventDefault()
      const href = anchor.getAttribute('href')
      if (!href || !href.startsWith('#')) return
      const id = href.slice(1)
      setActiveTocLink(nav, id)
      scrollToPolicySection(id)
    })
  })

  setupTocDisclosure(nav)
  setupScrollSpy(nav, headings)

  openFaqForHash(document, window.location.hash)
  window.addEventListener('hashchange', () => {
    openFaqForHash(document, window.location.hash)
  })

  nav.dataset.policyTocReady = 'true'
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPolicyToc())
} else {
  initPolicyToc()
}
